/**
 * Content Feedback Types
 *
 * TypeScript types for the content commenting and AI fix system.
 * Mirrors backend schema from content_feedback_service.py and ai_fix_service.py.
 */

// --- Enums ---

export type CommentStatus = 'open' | 'resolved' | 'ai_fixing' | 'ai_fixed' | 'dismissed';

export type CommentType =
	| 'general'
	| 'fix_request'
	| 'image_issue'
	| 'style_complaint'
	| 'factual_error'
	| 'link_issue';

export type AuthorRole = 'internal' | 'client' | 'ai_agent';

export type AIFixStatus =
	| 'pending'
	| 'processing'
	| 'approved'
	| 'rejected'
	| 'applied'
	| 'failed';

export type AIFixType =
	| 'text_rewrite'
	| 'image_regeneration'
	| 'style_change'
	| 'link_fix';

// --- Comment ---

export interface ContentComment {
	id: string;
	content_id: string;
	tenant_id: string;
	block_id: string | null;
	node_index: number | null;
	text_snippet: string | null;
	comment_text: string;
	comment_type: CommentType;
	parent_comment_id: string | null;
	thread_depth: number;
	author_id: string;
	author_name: string;
	author_role: AuthorRole;
	status: CommentStatus;
	ai_fix_id: string | null;
	created_at: string;
	updated_at: string;
}

/** Comment with nested replies array (used by CommentSidebar). */
export interface CommentWithReplies extends ContentComment {
	replies: ContentComment[];
}

/** API response: flat comments grouped by block_id. */
export type CommentsGroupedByBlock = Record<string, ContentComment[]>;

/** UI shape: comments with pre-structured replies grouped by block_id. */
export type CommentsWithRepliesByBlock = Record<string, CommentWithReplies[]>;

// --- Requests ---

export interface CreateCommentRequest {
	block_id?: string | null;
	node_index?: number | null;
	text_snippet?: string | null;
	comment_text: string;
	comment_type?: CommentType;
	parent_comment_id?: string | null;
	author_id?: string;
	author_name?: string;
	author_role?: AuthorRole;
}

export interface TriggerAIFixRequest {
	comment_id: string;
	fix_type?: AIFixType;
	block_id?: string | null;
	mode?: 'auto_apply' | 'suggest';
}

// --- AI Fix ---

export interface AIFix {
	id: string;
	content_id: string;
	tenant_id: string;
	source_comment_id: string | null;
	fix_type: AIFixType;
	block_id: string | null;
	original_content: unknown;
	proposed_content: unknown;
	applied_content: unknown;
	ai_model: string | null;
	mode: 'auto_apply' | 'suggest';
	status: AIFixStatus;
	reviewed_by: string | null;
	reviewed_at: string | null;
	created_at: string;
}

// --- UI Config ---

export const COMMENT_TYPE_CONFIG: Record<
	CommentType,
	{ label: string; color: string }
> = {
	general: { label: 'General', color: 'bg-gray-100 text-gray-700' },
	fix_request: {
		label: 'Fix Request',
		color: 'bg-coral-light text-coral-dark',
	},
	image_issue: { label: 'Image Issue', color: 'bg-amber-100 text-amber-700' },
	style_complaint: {
		label: 'Style',
		color: 'bg-teal-light text-teal-dark',
	},
	factual_error: {
		label: 'Factual Error',
		color: 'bg-destructive/10 text-destructive',
	},
	link_issue: { label: 'Link Issue', color: 'bg-blue-100 text-blue-700' },
};
